/**
 * MaterialsTab.tsx — optional Materials tab UI (MAT-04).
 *
 * CONDITIONAL RENDER: returns null when materialsTabEnabled is false.
 * This is the optional-complexity guard (CLAUDE.md rule 10 + MAT-04 BLOCKING).
 * The module stays in the bundle; the guard is a runtime conditional.
 *
 * When enabled, renders a BOM table for the active room:
 *  - material name, surfaceType, norm, qty, unit, price, lineTotal
 *  - per-line norm/qty override controls (writes to room.bomOverrides)
 *
 * Display rule: lineTotal is displayed AS-IS (pre-rounded to 2dp by buildBOM).
 * NEVER call roundTo in JSX — PDF-03 cardinal rule.
 * All strings via t('materials.<key>', 'Fallback') — no hardcoded text (CLAUDE.md rule 13).
 */

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/stores/useUIStore';
import { useProjectStore } from '@/stores/useProjectStore';
import { useMaterials } from '../hooks/useMaterials';
import type { Room } from '@/shared/model';
import type { BOMLine } from '@/entities/material';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MaterialsTabProps {
  /** The currently active room (with geometry attached by useEstimate) */
  room: Room | null;
  /** Material norms from the selected service or current room's services */
  norms: Array<{ materialId: string; norm: number; surfaceType?: string }>;
}

// ---------------------------------------------------------------------------
// Override row UI
// ---------------------------------------------------------------------------

interface OverrideCellProps {
  line: BOMLine;
  onOverrideQty: (materialId: string, qty: number) => void;
}

const OverrideCell: FC<OverrideCellProps> = ({ line, onOverrideQty }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (editing) {
    return (
      <input
        type="number"
        min="0"
        step="0.001"
        autoFocus
        className="w-20 border border-blue-400 rounded px-1 py-0.5 text-xs font-mono dark:bg-slate-700 dark:text-slate-100"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const v = parseFloat(draft);
          if (!Number.isNaN(v) && v >= 0) {
            onOverrideQty(line.materialId, v);
          }
          setEditing(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(String(line.quantity));
        setEditing(true);
      }}
      className={`px-2 py-0.5 text-xs rounded font-mono tabular-nums ${
        line.qtyIsManual
          ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300'
          : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/30 dark:hover:text-blue-300'
      }`}
      title={t('materials.override_qty', 'Override qty')}
    >
      {line.quantity.toFixed(3)}
    </button>
  );
};

// ---------------------------------------------------------------------------
// MaterialsTab
// ---------------------------------------------------------------------------

export const MaterialsTab: FC<MaterialsTabProps> = ({ room, norms }) => {
  const { t } = useTranslation();
  const materialsTabEnabled = useUIStore((s) => s.materialsTabEnabled);
  const currency = useUIStore((s) => s.currency);
  const updateRoom = useProjectStore((s) => s.updateRoom);

  // All hooks must be called before any early return (Rules of Hooks).
  // useMaterials is safe to call even when materialsTabEnabled=false —
  // it does no DOM work and returns an empty BOMTotalsResult.
  const { bomLines, materialTotals } = useMaterials(room, norms);

  // CONDITIONAL RENDER — BLOCKING guard (MAT-04, CLAUDE.md rule 10)
  // Must come AFTER all hooks.
  if (!materialsTabEnabled) return null;

  const handleOverrideQty = (materialId: string, qty: number) => {
    if (!room) return;
    const existingOverrides = room.bomOverrides || {};
    updateRoom(room.id, {
      bomOverrides: {
        ...existingOverrides,
        [materialId]: {
          normIsManual: existingOverrides[materialId]?.normIsManual ?? false,
          qty,
        },
      },
    });
  };

  if (bomLines.length === 0) {
    return (
      <div className="text-sm text-slate-400 dark:text-slate-500 py-4 text-center">
        {t('materials.no_materials', 'No material norms for the selected service')}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-800 text-white">
            <th className="px-2 py-2 text-left font-bold">{t('materials.col_material', 'Material')}</th>
            <th className="px-2 py-2 text-left font-bold">{t('materials.col_surface', 'Surface')}</th>
            <th className="px-2 py-2 text-right font-bold">{t('materials.col_norm', 'Norm')}</th>
            <th className="px-2 py-2 text-right font-bold">{t('materials.col_qty', 'Qty')}</th>
            <th className="px-2 py-2 text-center font-bold">{t('materials.col_unit', 'Unit')}</th>
            <th className="px-2 py-2 text-right font-bold">{t('materials.col_price', 'Price')}</th>
            <th className="px-2 py-2 text-right font-bold">{t('materials.col_total', 'Total')}</th>
          </tr>
        </thead>
        <tbody>
          {bomLines.map((line, idx) => {
            const isEven = idx % 2 === 0;
            return (
              <tr
                key={line.materialId}
                className={`border-b border-slate-200 dark:border-slate-700 ${
                  isEven ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50'
                }`}
              >
                <td className="px-2 py-1.5 font-medium text-slate-700 dark:text-slate-200">
                  {line.material.name}
                </td>
                <td className="px-2 py-1.5 text-slate-500 dark:text-slate-400">
                  {line.surfaceType}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600 dark:text-slate-300">
                  {line.norm.toFixed(3)}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <OverrideCell line={line} onOverrideQty={handleOverrideQty} />
                </td>
                <td className="px-2 py-1.5 text-center text-slate-500 dark:text-slate-400">
                  {line.material.unit}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-slate-600 dark:text-slate-300">
                  {line.pricePerUnit.toFixed(2)}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums font-bold text-slate-800 dark:text-slate-100">
                  {/* Display lineTotal AS-IS — pre-rounded to 2dp by buildBOM. NEVER re-round. */}
                  {line.lineTotal.toFixed(2)} {currency}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20">
            <td colSpan={6} className="px-2 py-2 text-right font-bold text-emerald-700 dark:text-emerald-300 text-sm">
              {t('materials.net_total', 'Materials net total:')}
            </td>
            <td className="px-2 py-2 text-right font-bold font-mono tabular-nums text-emerald-700 dark:text-emerald-300 text-sm">
              {/* materialTotals.netTotal = sum of already-rounded lineTotals. Display as-is. */}
              {materialTotals.netTotal.toFixed(2)} {currency}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
